import { useEffect, useReducer, useRef } from 'react';
import type { CloudVault } from '../vault/cloudVault';
import { itemFor } from './corpus';
import { durationInRows } from './duration';
import { readThought, tombstoneAt } from './thought';
import type { Thought } from './thought';

/**
 * The windowed read — the one mechanic this surface has that no other does.
 *
 * Every other surface reads a bounded set (91 arcs) or a single file. This one
 * is over ~4,900, so only what is on screen is fetched, in small batches, and
 * a row shows what the manifest gives for free until its file lands.
 *
 * Chunks are small on purpose. `readFiles` would happily take the whole
 * window in one call, but then nothing appears until all of it has decrypted;
 * at 24 the list fills as it reads.
 */

const CHUNK = 24;

export interface ThoughtWindow {
  get(path: string): Thought | undefined;
  /** How many of the requested paths have been opened. */
  read: number;
  /** Deletion markers found while reading — real files under a capture's name
   *  that no manifest can tell apart from live ones. */
  tombstones: number;
}

export function useThoughtWindow(vault: CloudVault | null, paths: string[]): ThoughtWindow {
  const cache = useRef(new Map<string, Thought>());
  const [, bump] = useReducer((n: number) => n + 1, 0);
  const wanted = paths.join(' ');

  useEffect(() => {
    cache.current = new Map();
    bump();
  }, [vault]);

  useEffect(() => {
    if (!vault) return;
    const missing = paths.filter((path) => !cache.current.has(path));
    if (missing.length === 0) return;

    let cancelled = false;
    // Nothing below may reject. An unhandled rejection out of an effect shows
    // nowhere in the UI and leaves the window stopped for the life of the tab.
    void fillWindow(vault, missing, cache.current, bump, () => cancelled).catch(() => {});

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vault, wanted]);

  let read = 0;
  let tombstones = 0;
  for (const path of paths) {
    const thought = cache.current.get(path);
    if (!thought) continue;
    read += 1;
    if (thought.tombstone) tombstones += 1;
  }

  return { get: (path) => cache.current.get(path), read, tombstones };
}

/**
 * Reads `paths` into `into` a chunk at a time, reporting after each so the list
 * fills as it goes rather than all at once.
 *
 * A chunk that cannot be READ is skipped and the run carries on. It used to
 * take the whole window down with it: one rejection ended the loop, every
 * remaining row stayed a skeleton for as long as the tab was open, and nothing
 * anywhere said why. Exported because that rule is the thing worth pinning, and
 * a hook cannot be driven from a test in this project.
 */
export async function fillWindow(
  vault: Pick<CloudVault, 'readFiles'>,
  paths: string[],
  into: Map<string, Thought>,
  onChunk: () => void,
  cancelled: () => boolean = () => false,
): Promise<void> {
  for (let i = 0; i < paths.length; i += CHUNK) {
    const chunk = paths.slice(i, i + CHUNK);
    let files;
    try {
      files = await vault.readFiles(chunk);
    } catch {
      // Skipped, never tombstoned: a chunk that could not be read says nothing
      // about whether those files exist, and marking them deleted would be a
      // lie that outlives the failure. They are asked for again the next time
      // what is on screen moves.
      if (cancelled()) return;
      continue;
    }
    if (cancelled()) return;
    for (const file of files) into.set(file.path, readThought(file.path, file.text));
    // A path the manifest no longer resolves must still count as read, or the
    // window asks for it again on every render.
    for (const path of chunk) {
      if (!into.has(path)) into.set(path, tombstoneAt(path));
    }
    onChunk();
    if (await fillDurations(vault, chunk, into)) {
      if (cancelled()) return;
      onChunk();
    }
  }
}

/** A recording whose note does not say how long it runs has it read from its
 *  rows sidecar, a chunk's worth in one read, after the cards are already up.
 *  Failing costs only the length. True when any length arrived. */
async function fillDurations(
  vault: Pick<CloudVault, 'readFiles'>,
  chunk: string[],
  into: Map<string, Thought>,
): Promise<boolean> {
  const owed = new Map<string, Thought>();
  for (const path of chunk) {
    const thought = into.get(path);
    if (thought?.rowsAt && itemFor(path).kind === 'recording') owed.set(thought.rowsAt, thought);
  }
  if (owed.size === 0) return false;
  const rows = await vault.readFiles([...owed.keys()]).catch(() => []);
  let found = false;
  for (const file of rows) {
    const thought = owed.get(file.path);
    const durationMs = durationInRows(file.text);
    if (!thought || durationMs === null) continue;
    into.set(thought.path, { ...thought, durationMs, rowsAt: null });
    found = true;
  }
  return found;
}
